import React, { useState, useEffect, useRef } from 'react';
import {
  FiFileText,
  FiUser,
  FiRefreshCw,
  FiEdit,
  FiCheckSquare,
  FiHelpCircle,
  FiClock,
  FiChevronUp,
  FiChevronDown,
} from 'react-icons/fi';
import { MdOutlineDescription, MdOutlineNumbers } from 'react-icons/md';
import { BsListCheck, BsCurrencyRupee } from 'react-icons/bs';
import { RiFileTextLine } from 'react-icons/ri';
import { TbBuildingStore } from 'react-icons/tb';
import { BiReceipt } from 'react-icons/bi';
import { AiOutlineClockCircle } from 'react-icons/ai';
import ComponentWrapper from '../components/utils/ComponentWrapper';
import InputField from '../components/ui/InputField';
import ImageUploader from '../components/ui/ImageUploader';
import { useAppSelector } from '../redux/hooks';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch } from '../redux/hooks';
import axios from 'axios';
import Loading from '../components/utils/Loading';
import { updateProject, createProject, loadProjects } from '../redux/actions/projectAction';
import toast from 'react-hot-toast';
import { uploadFileToS3 } from '../utils/s3Upload';
import { getValidStatusTransitions } from '../utils/workflowValidation';
import StatusTransitionHelpModal from '../components/ui/StatusTransitionHelpModal';
import TaskAssignmentTable from '../components/Projects/TaskAssignmentTable';
import {
  hasProjectEditPermission,
  hasProjectReadPermission,
  hasFinancePermission,
  hasChangeProjectNamePermission,
  hasChangeArticleNumberPermission,
  hasChangeCompanyNamePermission,
  hasTransferProjectPermission,
  hasChangeProjectDescriptionPermission,
  hasChangeProjectRemarksPermission,
  hasChangeProjectCostPermission,
  hasUploadProductImagePermission,
  hasChangeBillNumberPermission,
  hasChangeWarrantyPeriodPermission,
  hasChangeRevisionAmountPermission,
  hasChangeRevisionReasonPermission,
  hasChangeRevisionChargeablePermission,
  hasStatusPermission,
} from '../utils/permissionUtils';

// Define project data interface
interface ProjectData {
  projectName: string;
  articleNumber: string;
  customer: string;
  assignedEmployee: string;
  projectDescription: string;
  projectRemarks: string;
  projectStatus: string;
  projectCost: string;
  isSelfAssigned: boolean;
  projectImage: File | null;
  imagePreview: string | null;
  revisionReason: 'feed' | 'cad' | 'material';
  isChargeable: boolean;
  billNumber: string;
}

// New interface to track changes
interface ChangedFields {
  projectName: boolean;
  articleNumber: boolean;
  customer: boolean;
  assignedEmployee: boolean;
  projectDescription: boolean;
  projectRemarks: boolean;
  projectStatus: boolean;
  projectCost: boolean;
  isChargeable: boolean;
  revisionReason: boolean;
  billNumber: boolean;
}

const EditProject = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();

  // Get customer and employee data from Redux store
  const { customers, employees } = useAppSelector(state => state.users);
  // Get current user data from auth state
  const { user } = useAppSelector(state => state.auth);

  // Define isAdmin at component level so it's available throughout the component
  const isAdmin = user?.designation === 'superAdmin' || user?.designation === 'admin';

  // State to track if current user is assigned to this project
  const [isUserAssignedToProject, setIsUserAssignedToProject] = useState(false);
  const [isUserManagerOfAssignee, setIsUserManagerOfAssignee] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [projectLoaded, setProjectLoaded] = useState(false);
  // Only render the form after the access guard has confirmed permission.
  // Prevents a one-frame data flash before the redirect fires.
  const [isAccessChecked, setIsAccessChecked] = useState(false);

  // State for Manak ID display
  const [manakId, setManakId] = useState<string>('');

  // Debug log for manakId
  useEffect(() => {
    console.log('Current manakId state:', manakId);
  }, [manakId]);

  // Search and dropdown states
  const [customerSearch, setCustomerSearch] = useState('');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  // Refs for detecting outside clicks
  const customerDropdownRef = useRef<HTMLDivElement>(null);
  const employeeDropdownRef = useRef<HTMLDivElement>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);

  // Project status options based on Prisma schema
  // Note: BILLED will be shown but disabled - it should primarily be set via invoice creation
  const allProjectStatuses = [
    'UNASSIGNED',
    'ASSIGNED',
    'IN_PROGRESS',
    'REVIEW',
    'DELIVERED',
    'BILLED',
    'HOLD',
    'CANCELLED',
    'PAYMENT_RECEIVED',
    'REOPEN',
    'NON_ACTIVE',
  ];

  // Function to format status values for display
  const formatStatus = (status: string): string => {
    if (!status) return '';

    // Replace underscores with spaces
    let formattedStatus = status.replace(/_/g, ' ');

    // Convert to title case (capitalize first letter of each word)
    formattedStatus = formattedStatus
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');

    // Handle special cases
    switch (status.toUpperCase()) {
      case 'IN_PROGRESS':
        return 'In Progress';
      case 'PAYMENT_RECEIVED':
        return 'Payment Received';
      default:
        return formattedStatus;
    }
  };

  // Function to get filtered status options based on user role and permissions
  const getStatusOptions = () => {
    // If admin, show all statuses
    if (isAdmin) {
      return allProjectStatuses;
    }

    // Filter statuses using the same hasStatusPermission helper used elsewhere,
    // which correctly handles all permission object formats ({0:{...}}, array, flat).
    const filteredStatuses = allProjectStatuses.filter(status => {
      if (status === 'UNASSIGNED') return true;
      if (status === 'NON_ACTIVE') return false;
      return hasStatusPermission(user, status);
    });

    return filteredStatuses;
  };

  // Get filtered statuses based on user permissions
  const statusOptions = getStatusOptions();

  // Project data from navigation state
  const projectDataFromState = location.state?.projectData;
  const returnPath: string = location.state?.returnPath || '/projects';

  // Loading state
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // State for status transition help modal
  const [showStatusHelpModal, setShowStatusHelpModal] = useState(false);

  // Track which fields have been modified
  const [changedFields, setChangedFields] = useState<ChangedFields>({
    projectName: false,
    articleNumber: false,
    customer: false,
    assignedEmployee: false,
    projectDescription: false,
    projectRemarks: false,
    projectStatus: false,
    projectCost: false,
    isChargeable: false,
    revisionReason: false,
    billNumber: false,
  });

  // Original data to compare against
  const [originalData, setOriginalData] = useState<Partial<ProjectData>>({});

  // Single project state object
  const [projectData, setProjectData] = useState<ProjectData>({
    projectName: '',
    articleNumber: '',
    customer: '',
    assignedEmployee: '',
    projectDescription: '',
    projectRemarks: '',
    projectStatus: '',
    projectCost: '',
    isSelfAssigned: false,
    projectImage: null,
    imagePreview: null,
    revisionReason: 'feed',
    isChargeable: false,
    billNumber: '',
  });

  const [revisionAmount, setRevisionAmount] = useState('');
  const [warrantyPeriod, setWarrantyPeriod] = useState('');
  const [originalWarrantyPeriod, setOriginalWarrantyPeriod] = useState('');
  const [billedDate, setBilledDate] = useState<Date | null>(null);
  const [isImageMarkedForDeletion, setIsImageMarkedForDeletion] = useState(false);

  // State for showing assignment history
  const [showAssignmentHistory, setShowAssignmentHistory] = useState(false);
  const [assignmentHistory, setAssignmentHistory] = useState([]);

  // Function to calculate days between two dates
  const calculateDaysBetween = (date1: Date, date2: Date): number => {
    // Create date objects without time components to avoid timezone issues
    const startDate = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate());
    const endDate = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate());

    // Calculate difference in milliseconds
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());

    // Convert to days (without adding 1, since warranty period is the duration)
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  // Helper function to format assignment history for display
  const formatAssignmentHistory = (project: any, employeesList: any[], currentUserData: any) => {
    if (!project?.projectAssigned || project.projectAssigned.length === 0) {
      return [];
    }

    // Sort assignments by date (newest first)
    const sortedAssignments = [...project.projectAssigned].sort((a, b) => {
      const dateA = new Date(b.assignedDate || b.assignedDateAndTime || 0).getTime();
      const dateB = new Date(a.assignedDate || a.assignedDateAndTime || 0).getTime();
      return dateA - dateB;
    });

    // Return the raw assignments - let the TaskAssignmentTable component handle all formatting
    return sortedAssignments;
  };

  // Filter customers based on search and sort alphabetically
  const customerOptions = customers
    .filter(c => c.companyName.toLowerCase().includes(customerSearch.toLowerCase()))
    .sort((a, b) => a.companyName.localeCompare(b.companyName))
    .map(c => c.companyName);

  // Filter employees based on search
  const employeeOptions = employees
    .filter(e => {
      // Always include the current user (You option) - never filter it out
      if (e.id === user?.id) return true;

      // If this employee is selected and matches the search, include them
      if (projectData.assignedEmployee && e.id === projectData.assignedEmployee) {
        // Check if search matches the selected employee
        if (employeeSearch) {
          const searchLower = employeeSearch.toLowerCase();
          const nameLower = e.fullname.toLowerCase();
          // Handle "You (name)" pattern for current user
          if (e.id === user?.id) {
            const youPattern = `you (${nameLower})`;
            return (
              nameLower.includes(searchLower) ||
              youPattern.includes(searchLower) ||
              searchLower === 'you'
            );
          }
          return nameLower.includes(searchLower);
        }
        return false; // If no search and employee is selected, don't show in dropdown
      }

      // Apply search filter for non-selected employees
      if (employeeSearch) {
        const searchLower = employeeSearch.toLowerCase();
        const nameLower = e.fullname.toLowerCase();
        // Handle "You (name)" pattern for current user
        if (e.id === user?.id) {
          const youPattern = `you (${nameLower})`;
          return (
            nameLower.includes(searchLower) ||
            youPattern.includes(searchLower) ||
            searchLower === 'you'
          );
        }
        return nameLower.includes(searchLower);
      }

      return true;
    })
    .sort((a, b) => {
      // If search is empty, sort alphabetically
      if (!employeeSearch) return a.fullname.localeCompare(b.fullname);

      const aStartsWithSearch = a.fullname.toLowerCase().startsWith(employeeSearch.toLowerCase());
      const bStartsWithSearch = b.fullname.toLowerCase().startsWith(employeeSearch.toLowerCase());

      // Sort by whether the item starts with the search term
      if (aStartsWithSearch && !bStartsWithSearch) return -1;
      if (!aStartsWithSearch && bStartsWithSearch) return 1;

      // If both start with search or neither starts with search, sort by whether it contains the search term
      const aContainsSearch = a.fullname.toLowerCase().includes(employeeSearch.toLowerCase());
      const bContainsSearch = b.fullname.toLowerCase().includes(employeeSearch.toLowerCase());

      if (aContainsSearch && !bContainsSearch) return -1;
      if (!aContainsSearch && bContainsSearch) return 1;

      // If both match equally, sort alphabetically
      return a.fullname.localeCompare(b.fullname);
    })
    .map(e => e.fullname);

  // New state for reopened project assignment
  const [reopenAssignment, setReopenAssignment] = useState({
    revisionAssignedTo: '',
    revisionAssignedToName: '',
    revisionNewStatus: 'ASSIGNED', // Default to ASSIGNED
    isSelfAssigned: false,
  });

  // Update a single field in the project data
  const updateProjectField = (field: keyof ProjectData, value: any) => {
    setProjectData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Check if the field is one we need to track for changes
    if (field in changedFields) {
      // Compare with original data to see if it's actually changed
      const hasChanged = originalData[field] !== value;
      setChangedFields(prev => ({
        ...prev,
        [field]: hasChanged,
      }));
    }
  };

  // Returns true if the current user is the direct manager of the latest assignee.
  const checkUserIsManagerOfAssignee = (projectData: any): boolean => {
    if (!user?.id || !projectData?.projectAssigned?.length) return false;
    const sorted = [...projectData.projectAssigned].sort((a: any, b: any) => {
      return new Date(b.assignedDate || b.assignedDateAndTime || 0).getTime() -
             new Date(a.assignedDate || a.assignedDateAndTime || 0).getTime();
    });
    const latestAssigneeId = sorted[0]?.employeeId || sorted[0]?.assignedToEmployee;
    if (!latestAssigneeId || latestAssigneeId === user.id) return false;
    const assignee = employees.find((e: any) => e.id === latestAssigneeId);
    return assignee?.managerId === user.id;
  };

  // Returns true only if the current user is the LATEST (current) assignee of the project.
  // Historical assignments are ignored, matching the backend's own access-control check.
  const checkUserProjectAssignment = (projectData: any): boolean => {
    if (!user?.id || !projectData) return false;

    if (projectData.projectAssigned && projectData.projectAssigned.length > 0) {
      // Sort descending by date and inspect only the most recent record.
      const sorted = [...projectData.projectAssigned].sort((a, b) => {
        const dateA = new Date(a.assignedDate || a.assignedDateAndTime || 0).getTime();
        const dateB = new Date(b.assignedDate || b.assignedDateAndTime || 0).getTime();
        return dateB - dateA;
      });

      const latest = sorted[0];
      return (
        latest.adminId === user.id ||
        latest.employeeId === user.id ||
        latest.assignedToEmployee === user.id
      );
    }

    // Fallback for flatter data shapes (no assignment history array)
    if (projectData.assignedEmployeeId === user.id) return true;
    if (projectData.assignedToEmployee === user.id) return true;
    if (projectData.assignedEmployee === user.id) return true;
    if (projectData.isSelfAssigned && projectData.adminId === user.id) return true;

    return false;
  };

  // Update the useEffect that handles project data from navigation state
  useEffect(() => {
    // If we have project data from navigation state, use it
    if (projectDataFromState) {
      console.log('Using project data from navigation state:', projectDataFromState);

      // Set Manak ID for display from Redux state
      const manakProjectId = projectDataFromState.manakProjectId ||
        projectDataFromState.manakId ||
        projectDataFromState.id?.toString()?.slice(-4) ||
        'N/A';
      console.log('Setting Manak ID from Redux to:', manakProjectId);
      setManakId(manakProjectId);

      // Debug log to help identify where the remark and status are stored
      console.log('Project remark fields:', {
        projectRemark: projectDataFromState.projectRemark,
        comments: projectDataFromState.comments,
        remark: projectDataFromState.remark,
      });

      console.log('Project status fields:', {
        status: projectDataFromState.status,
        displayStatus: projectDataFromState.displayStatus,
        latestStatus: projectDataFromState.projectAssigned?.[0]?.status,
      });

      // Check if the Projects page explicitly told us the user is assigned to this project
      if (projectDataFromState.userIsAssignedToThisProject === true) {
        console.log('User is explicitly marked as assigned to this project from Projects page');
        setIsUserAssignedToProject(true);
      } else {
        // Fallback to our own check
        const isAssigned = checkUserProjectAssignment(projectDataFromState);
        setIsUserAssignedToProject(isAssigned);
        console.log('User project assignment check result:', isAssigned);
      }
      setIsUserManagerOfAssignee(checkUserIsManagerOfAssignee(projectDataFromState));

      // Format assignment history for display
      if (projectDataFromState.projectAssigned && projectDataFromState.projectAssigned.length > 0) {
        const history = formatAssignmentHistory(projectDataFromState, employees, user);
        setAssignmentHistory(history);
      } else {
        setAssignmentHistory([]);
      }

      // Get the latest status from projectAssigned array if available
      let latestStatus = projectDataFromState.status || '';
      if (projectDataFromState.projectAssigned && projectDataFromState.projectAssigned.length > 0) {
        // Sort by date to get the most recent status
        const sortedAssignments = [...projectDataFromState.projectAssigned].sort((a, b) => {
          const dateA = new Date(a.assignedDate || a.assignedDateAndTime || 0).getTime();
          const dateB = new Date(b.assignedDate || b.assignedDateAndTime || 0).getTime();
          return dateB - dateA; // Descending order (newest first)
        });

        if (sortedAssignments[0]?.status) {
          latestStatus = sortedAssignments[0].status;
          console.log('Using latest status from projectAssigned:', latestStatus);
        }
      }

      // Set form fields with existing project data
      setProjectData({
        projectName: projectDataFromState.projectName || '',
        articleNumber: projectDataFromState.projectNumber || '',
        customer: projectDataFromState.ClientId || '',
        assignedEmployee: '',
        projectDescription: projectDataFromState.projectDescription || '',
        // Check all possible remark field names
        projectRemarks:
          projectDataFromState.projectRemark ||
          projectDataFromState.comments ||
          projectDataFromState.remark ||
          '',
        // Use the latest status we determined above
        projectStatus: latestStatus,
        projectCost: projectDataFromState.projectAmount?.toString() || '',
        isSelfAssigned: false,
        projectImage: null,
        imagePreview:
          projectDataFromState.profilePicture || projectDataFromState.projectImages?.[0] || null,
        revisionReason: 'feed',
        isChargeable: !!projectDataFromState.isChargeable,
        billNumber: projectDataFromState.billNumber || '',
      });

      // Handle project assignment and determine assigned employee for originalData
      let originalAssignedEmployee = '';
      let originalIsSelfAssigned = false;

      if (projectDataFromState.projectAssigned && projectDataFromState.projectAssigned.length > 0) {
        const assignment = projectDataFromState.projectAssigned[0];
        console.log('Project assignment data from state:', assignment);

        // Check for adminId (self-assignment)
        if (assignment.adminId) {
          console.log('This is a self-assigned project to admin:', assignment.adminId);
          // Find current user's full name for display
          const currentUser = employees.find(e => e.id === assignment.adminId);
          const displayText = currentUser ? `You (${currentUser.fullname})` : 'Self Assign';
          setEmployeeSearch(displayText);
          updateProjectField('isSelfAssigned', true);
          updateProjectField('assignedEmployee', assignment.adminId);
          originalAssignedEmployee = assignment.adminId;
          originalIsSelfAssigned = true;
        } else if (assignment.employeeId || assignment.assignedToEmployee) {
          console.log(
            'Project assigned to employee ID:',
            assignment.employeeId || assignment.assignedToEmployee
          );
          const employeeId = assignment.employeeId || assignment.assignedToEmployee;
          updateProjectField('assignedEmployee', employeeId);
          originalAssignedEmployee = employeeId;

          // Find employee name from Redux store
          if (employees.length > 0) {
            const matchingEmployee = employees.find(e => e.id === assignment.employeeId);
            if (matchingEmployee) {
              console.log('Found matching employee:', matchingEmployee);
              // Check if this employee is the logged-in user
              const isCurrentUser = matchingEmployee.id === user?.id;
              setEmployeeSearch(
                isCurrentUser ? `You (${matchingEmployee.fullname})` : matchingEmployee.fullname
              );
            } else {
              // If no matching employee but we have an ID, show the ID
              setEmployeeSearch(`Employee ID: ${assignment.employeeId}`);
            }
          }
        } else {
          // Fallback for empty assignment
          setEmployeeSearch('Not assigned');
        }
      }

      // Set original data for change tracking AFTER determining employee assignment
      setOriginalData({
        projectName: projectDataFromState.projectName || '',
        articleNumber: projectDataFromState.projectNumber || '',
        customer: projectDataFromState.ClientId || '',
        assignedEmployee: originalAssignedEmployee,
        projectDescription: projectDataFromState.projectDescription || '',
        // Check all possible remark field names
        projectRemarks:
          projectDataFromState.projectRemark ||
          projectDataFromState.comments ||
          projectDataFromState.remark ||
          '',
        // Use the latest status we determined above
        projectStatus: latestStatus,
        projectCost: projectDataFromState.projectAmount?.toString() || '',
        isSelfAssigned: originalIsSelfAssigned,
        projectImage: null,
        imagePreview:
          projectDataFromState.profilePicture || projectDataFromState.projectImages?.[0] || null,
        revisionReason: 'feed',
        isChargeable: !!projectDataFromState.isChargeable,
        billNumber: projectDataFromState.billNumber || '',
      });

      // Find customer name for display
      if (projectDataFromState.ClientId && customers.length > 0) {
        const matchingCustomer = customers.find(c => c.id === projectDataFromState.ClientId);
        if (matchingCustomer) {
          setCustomerSearch(matchingCustomer.companyName);
        }
      }

      // Find the billed date and calculate warranty period
      findBilledDateAndCalculateWarranty(projectDataFromState);

      setIsLoading(false);
      setProjectLoaded(true);
      return;
    }

    // If no data from state, continue with API fetch
    const fetchProjectData = async () => {
      try {
        setIsLoading(true);

        // Get token from auth state for API request (check both admin and employee tokens)
        const adminToken = document.cookie
          .split('; ')
          .find(row => row.startsWith('pms_admin_='))
          ?.split('=')[1];

        const employeeToken = document.cookie
          .split('; ')
          .find(row => row.startsWith('pms_employee_='))
          ?.split('=')[1];

        const token = adminToken || employeeToken;

        if (!token) {
          setLoadError('Authentication token not found');
          setIsLoading(false);
          return;
        }

        const response = await axios.get(`/api/v1/admin/project/${projectId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.success) {
          const projectData = response.data.data;
          console.log('Project data from API:', projectData);

          // Set Manak ID for display
          const manakProjectId = projectData.manakProjectId ||
            projectData.manakId ||
            projectData.id?.toString()?.slice(-4) ||
            'N/A';
          console.log('Setting Manak ID to:', manakProjectId);
          setManakId(manakProjectId);

          // Debug log to help identify where the remark and status are stored
          console.log('API Project remark fields:', {
            projectRemark: projectData.projectRemark,
            comments: projectData.comments,
            remark: projectData.remark,
          });

          console.log('API Project status fields:', {
            status: projectData.status,
            displayStatus: projectData.displayStatus,
            latestStatus: projectData.projectAssigned?.[0]?.status,
          });

          // Check if current user is assigned to this project
          const isAssigned = checkUserProjectAssignment(projectData);
          setIsUserAssignedToProject(isAssigned);
          setIsUserManagerOfAssignee(checkUserIsManagerOfAssignee(projectData));
          console.log('User is assigned to project:', isAssigned);

          // Get the latest status from projectAssigned array if available
          let latestStatus = projectData.status || '';
          if (projectData.projectAssigned && projectData.projectAssigned.length > 0) {
            // Sort by date to get the most recent status
            const sortedAssignments = [...projectData.projectAssigned].sort((a, b) => {
              const dateA = new Date(a.assignedDate || a.assignedDateAndTime || 0).getTime();
              const dateB = new Date(b.assignedDate || b.assignedDateAndTime || 0).getTime();
              return dateB - dateA; // Descending order (newest first)
            });

            if (sortedAssignments[0]?.status) {
              latestStatus = sortedAssignments[0].status;
              console.log('Using latest status from API projectAssigned:', latestStatus);
            }
          }

          // Set form fields with existing project data
          setProjectData({
            projectName: projectData.projectName || '',
            articleNumber: projectData.projectNumber || '',
            customer: projectData.ClientId || '',
            assignedEmployee: '',
            projectDescription: projectData.projectDescription || '',
            // Check all possible remark field names
            projectRemarks:
              projectData.projectRemark || projectData.comments || projectData.remark || '',
            // Use the latest status we determined above
            projectStatus: latestStatus,
            projectCost: projectData.projectAmount?.toString() || '',
            isSelfAssigned: false,
            projectImage: null,
            imagePreview:
              projectData.profilePicture &&
                projectData.profilePicture.trim() !== '' &&
                !projectData.profilePicture.includes('default-project-image.png')
                ? projectData.profilePicture
                : projectData.projectImages?.[0] || null,
            revisionReason: 'feed',
            isChargeable: !!projectData.isChargeable,
            billNumber: projectData.billNumber || '',
          });

          // Handle project assignment
          if (projectData.projectAssigned && projectData.projectAssigned.length > 0) {
            const assignment = projectData.projectAssigned[0];
            console.log('Project assignment data from API:', assignment);

            // Check for adminId (self-assignment)
            if (assignment.adminId) {
              console.log('This is a self-assigned project to admin:', assignment.adminId);
              // Find current user's full name for display
              const currentUser = employees.find(e => e.id === assignment.adminId);
              const displayText = currentUser ? `You (${currentUser.fullname})` : 'Self Assign';
              setEmployeeSearch(displayText);
              updateProjectField('isSelfAssigned', true);
              updateProjectField('assignedEmployee', assignment.adminId);
            } else if (assignment.employeeId) {
              console.log('Project assigned to employee ID:', assignment.employeeId);
              updateProjectField('assignedEmployee', assignment.employeeId);

              // Find employee name from Redux store
              if (employees.length > 0) {
                const matchingEmployee = employees.find(e => e.id === assignment.employeeId);
                if (matchingEmployee) {
                  console.log('Found matching employee:', matchingEmployee);
                  // Check if this employee is the logged-in user
                  const isCurrentUser = matchingEmployee.id === user?.id;
                  setEmployeeSearch(
                    isCurrentUser ? `You (${matchingEmployee.fullname})` : matchingEmployee.fullname
                  );
                } else {
                  setEmployeeSearch(`Employee ID: ${assignment.employeeId}`);
                }
              }
            } else {
              setEmployeeSearch('Not assigned');
            }
          }

          // Find customer name for display
          if (projectData.ClientId && customers.length > 0) {
            const matchingCustomer = customers.find(c => c.id === projectData.ClientId);
            if (matchingCustomer) {
              setCustomerSearch(matchingCustomer.companyName);
            }
          }

          // Find the billed date and calculate warranty period
          findBilledDateAndCalculateWarranty(projectData);
        } else {
          setLoadError('Failed to load project data');
        }
      } catch (error: any) {
        console.error('Error fetching project data:', error);
        setLoadError(
          error.response?.data?.message || 'An error occurred while loading the project'
        );
      } finally {
        setIsLoading(false);
        setProjectLoaded(true);
      }
    };

    if (projectId) {
      fetchProjectData();
    } else {
      setLoadError('No project ID provided');
      setIsLoading(false);
    }
  }, [projectId, projectDataFromState, customers, employees, user?.id]);

  // Access guard: redirect if user doesn't have permission to edit this project.
  // isAccessChecked is only set to true when access is confirmed, so the form
  // stays hidden (behind the loading spinner) until this effect runs.
  useEffect(() => {
    if (!projectLoaded) return;

    const canEditAll = user?.role === 'ADMIN' || user?.designation === 'superAdmin';

    if (!hasProjectEditPermission(user)) {
      toast.error("You don't have permission to edit projects");
      navigate(-1);
      return;
    }

    // Can edit this specific project if: admin, assigned to them, manager of assignee, or finance
    const canEditThisProject = canEditAll || isUserAssignedToProject || isUserManagerOfAssignee || hasFinancePermission(user);

    if (!canEditThisProject) {
      // If they have read permission, show the form in read-only mode instead of redirecting
      if (hasProjectReadPermission(user)) {
        setIsReadOnly(true);
      } else {
        toast.error("You can only edit projects that are currently assigned to you");
        navigate(-1);
        return;
      }
    } else {
      // Reset read-only if access is re-evaluated and user now qualifies (e.g. employees loaded late)
      setIsReadOnly(false);
    }

    // All checks passed — allow the form to render.
    setIsAccessChecked(true);
  }, [projectLoaded, isUserAssignedToProject, isUserManagerOfAssignee, user, navigate]);

  const handleImageUpload = (file: File) => {
    // Check file size before processing - limit to 5MB
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image file is too large. Please use an image smaller than 5MB.');
      return;
    }

    // Reset the marked for deletion state when a new image is uploaded
    setIsImageMarkedForDeletion(false);
    updateProjectField('projectImage', file);
    const reader = new FileReader();
    reader.onload = () => {
      updateProjectField('imagePreview', reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleImageRemove = () => {
    // Mark the image for deletion and clear the preview
    setIsImageMarkedForDeletion(true);
    updateProjectField('projectImage', null);
    updateProjectField('imagePreview', null);
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);

      // Check if revision is chargeable but no amount specified
      if (
        projectData.projectStatus === 'REOPEN' &&
        projectData.isChargeable &&
        !revisionAmount.trim()
      ) {
        toast.error('Please specify an amount for the new revision project');
        setIsLoading(false);
        return;
      }

      // Check if an employee is assigned (for any project status)
      if (!projectData.assignedEmployee) {
        toast.error('Please select an employee to assign the project to');
        setIsLoading(false);
        return;
      }

      // Prepare assignedToEmployee based on whether it's self-assigned or assigned to an employee
      let assignedToEmployee;

      if (projectData.isSelfAssigned) {
        assignedToEmployee = user?.id || projectData.assignedEmployee;
      } else {
        assignedToEmployee = projectData.assignedEmployee;
      }

      // Ensure status is properly normalized to match backend expectations
      const normalizedStatus = projectData.projectStatus.toUpperCase();
      console.log('Selected status (normalized):', normalizedStatus);

      // Create an object to hold only the changed fields
      const updateData: any = {};

      // Always include projectAssigned if status has changed OR employee has changed
      const statusChanged =
        changedFields.projectStatus ||
        originalData.projectStatus?.toUpperCase() !== normalizedStatus;

      const employeeChanged = changedFields.assignedEmployee;

      if (statusChanged || employeeChanged) {
        // Create a new projectAssigned entry if status or employee has changed
        const noteMessage =
          statusChanged && employeeChanged
            ? `Status updated to ${normalizedStatus} and reassigned from edit project page`
            : statusChanged
              ? `Status updated to ${normalizedStatus} from edit project page`
              : `Project reassigned ${projectData.isSelfAssigned ? 'to self' : 'to employee'} from edit project page`;

        updateData.projectAssigned = [
          {
            assignedToEmployee: assignedToEmployee,
            assignedDateAndTime: new Date().toISOString(),
            status: normalizedStatus,
            note: noteMessage,
          },
        ];

        if (statusChanged) {
          updateData.status = normalizedStatus;
        }

        // When status is changed to BILLED, calculate warranty until date
        if (normalizedStatus === 'BILLED' && warrantyPeriod) {
          // Calculate the warranty until date
          const warrantyDays = parseInt(warrantyPeriod, 10);
          if (!isNaN(warrantyDays) && warrantyDays > 0) {
            const warrantyDate = new Date();
            warrantyDate.setDate(warrantyDate.getDate() + warrantyDays);
            updateData.warrantyUntil = warrantyDate.toISOString();
          }
        }
      }

      // If status is already BILLED and warranty period is updated
      if (normalizedStatus === 'BILLED' && warrantyPeriod && !statusChanged) {
        // Calculate the warranty until date
        const warrantyDays = parseInt(warrantyPeriod, 10);
        if (!isNaN(warrantyDays) && warrantyDays > 0) {
          const warrantyDate = new Date();
          warrantyDate.setDate(warrantyDate.getDate() + warrantyDays);
          updateData.warrantyUntil = warrantyDate.toISOString();
        }
      }

      // Check if warranty period has changed (for any status, not just BILLED)
      if (warrantyPeriod && warrantyPeriod !== originalWarrantyPeriod) {
        const warrantyDays = parseInt(warrantyPeriod, 10);
        if (!isNaN(warrantyDays) && warrantyDays > 0) {
          updateData.warrantyPeriodDays = warrantyDays;
          // Also calculate warrantyUntil if there's a billed date
          if (billedDate) {
            const warrantyDate = new Date(billedDate);
            warrantyDate.setDate(warrantyDate.getDate() + warrantyDays);
            updateData.warrantyUntil = warrantyDate.toISOString();
          }
        }
      }

      // Check each field and only include it if it has changed
      if (changedFields.projectName) {
        updateData.projectName = projectData.projectName;
      }

      if (changedFields.articleNumber) {
        updateData.projectNumber = projectData.articleNumber;
      }

      if (changedFields.customer) {
        updateData.ClientId = projectData.customer;
      }

      if (changedFields.projectDescription) {
        updateData.projectDescription = projectData.projectDescription;
      }

      if (changedFields.projectRemarks) {
        // Send the remark value to both fields for compatibility
        updateData.projectRemark = projectData.projectRemarks;
        updateData.comments = projectData.projectRemarks;
      }

      if (changedFields.projectCost) {
        updateData.projectAmount = parseFloat(projectData.projectCost) || 0;
      }

      // Always add revision details if the project status is changing to REOPEN
      if (normalizedStatus === 'REOPEN' && statusChanged) {
        updateData.revisionNumber = (projectDataFromState?.revisionNumber || 0) + 1;
        updateData.revisionReson = projectData.revisionReason;
        updateData.isChargeable = projectData.isChargeable;

        if (projectData.isChargeable) {
          if (projectData.billNumber) {
            updateData.billNumber = projectData.billNumber;
          }

          // Add the revision amount for chargeable reopens
          if (revisionAmount) {
            updateData.revisionAmount = parseFloat(revisionAmount) || 0;
          }
        }
      } else {
        // If not reopening but these fields have changed
        if (changedFields.revisionReason) {
          updateData.revisionReson = projectData.revisionReason;
        }

        if (changedFields.isChargeable) {
          updateData.isChargeable = projectData.isChargeable;
        }

        // Only include billNumber if it has actually changed
        if (changedFields.billNumber) {
          updateData.billNumber = projectData.billNumber;
        }
      }

      // Handle image deletion if marked for deletion (only if no new image is being uploaded)
      if (isImageMarkedForDeletion) {
        // Set profilePicture to null to delete the image
        updateData.profilePicture = null;
        console.log('Image marked for deletion, setting profilePicture to null');
      }

      // Handle image upload if there's a new image (this will override the deletion)
      if (projectData.projectImage instanceof File) {
        try {
          console.log('Uploading new image for project update:', projectData.projectImage.name);

          // Upload the image to S3 using pre-signed URL approach
          const imageUrl = await uploadFileToS3(projectData.projectImage);

          console.log('Image uploaded successfully for project update, URL:', imageUrl);

          // Add the image URL to the update data
          updateData.profilePicture = imageUrl;
        } catch (uploadError) {
          console.error('Error uploading image during project update:', uploadError);
          toast.error('Failed to upload image. Please try again.');
          setIsLoading(false);
          return;
        }
      }

      // Check if there are any changes to update
      if (Object.keys(updateData).length === 0) {
        toast('No changes detected. Nothing to update.');
        setIsLoading(false);
        // Don't navigate away immediately - let user stay on the edit page
        return;
      }

      console.log('Sending only changed data:', JSON.stringify(updateData, null, 2));

      // Determine the project ID to use (from params or state)
      const idToUse = projectId || projectDataFromState?.id;

      if (!idToUse) {
        toast.error('Project ID is missing');
        setIsLoading(false);
        return;
      }

      // Show loading toast
      const loadingToastId = toast.loading('Updating project...');

      try {
        // For reopened projects, handle the assignment data appropriately
        if (projectData.projectStatus === 'REOPEN') {
          // Always send REOPEN status from frontend
          // The backend will handle converting non-chargeable projects to ASSIGNED
          updateData.projectAssigned = [
            {
              assignedToEmployee: projectData.assignedEmployee,
              assignedDateAndTime: new Date().toISOString(),
              status: 'REOPEN', // Always keep REOPEN status from frontend
              note: `Project reopened with reason: ${projectData.revisionReason} ${projectData.isChargeable ? '(Chargeable)' : '(Non-chargeable)'}`,
            },
          ];
          // Keep the REOPEN status for the backend to handle
          updateData.status = 'REOPEN';
        }

        // Use Redux action to update project
        const result = await dispatch(
          updateProject({
            projectId: idToUse,
            projectData: updateData,
          })
        ).unwrap();

        // Success case
        toast.dismiss(loadingToastId);

        // Check if the backend created a new project for a chargeable revision
        if (normalizedStatus === 'REOPEN' && projectData.isChargeable) {
          if (result?.data?.project?.reopenedProjectId) {
            // The backend has created a new project
            console.log(
              'Backend created a new project with ID:',
              result.data.project.reopenedProjectId
            );
            toast.success('Project updated and new revision project created successfully!');
          } else {
            // Just show the regular success message
            toast.success('Project updated successfully!');
          }
        } else {
          toast.success('Project updated successfully!');
        }

        // Reload projects in the background to ensure state is up-to-date
        dispatch(loadProjects());

        // Navigate back to the originating page after a brief delay to allow toasts to be seen
        setTimeout(() => {
          navigate(returnPath);
        }, 1500);
      } catch (error: any) {
        // Dismiss the loading toast and show error
        toast.dismiss(loadingToastId);

        // Log detailed error information for debugging
        console.error('Error updating project:', error);
        console.error('Error response:', error.response?.data);

        // Handle different error formats.
        // dispatch(...).unwrap() throws the raw rejection payload (a string) when
        // rejectWithValue is used, so check typeof first before accessing .message.
        const errorMessage =
          (typeof error === 'string' ? error : null) ||
          error?.message ||
          error?.response?.data?.error?.message ||
          'An error occurred while updating the project';

        toast.error(errorMessage);
        setIsLoading(false); // Ensure loading state is reset
      }
    } catch (error: any) {
      console.error('Error in submit handler:', error);
      toast.error('An unexpected error occurred. Please try again.');
      setIsLoading(false); // Reset loading state
    }
  };

  // Handle customer search input
  const handleCustomerSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomerSearch(e.target.value);
    setShowCustomerDropdown(true);
    // Also update the main customer selection if exact match
    const exactMatch = customers.find(
      c => c.companyName.toLowerCase() === e.target.value.toLowerCase()
    );
    if (exactMatch) {
      updateProjectField('customer', exactMatch.id);
    }
  };

  // Handle employee search input
  const handleEmployeeSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmployeeSearch(value);
    setShowEmployeeDropdown(true);

    // Check if input matches "You" or "You (" pattern and convert to current user
    if ((value.toLowerCase() === 'you' || value.toLowerCase().startsWith('you (')) && user?.id) {
      const currentUser = employees.find(e => e.id === user.id);
      if (currentUser) {
        // Update data with current user's info
        updateProjectField('assignedEmployee', currentUser.id);
        updateProjectField('isSelfAssigned', false);
        return;
      }
    }

    // Handle "You (name)" format input
    const youPattern = /^You \((.+)\)$/;
    const youMatch = value.match(youPattern);
    if (youMatch && user?.id) {
      const currentUser = employees.find(e => e.id === user.id);
      if (currentUser && currentUser.fullname === youMatch[1]) {
        updateProjectField('assignedEmployee', currentUser.id);
        updateProjectField('isSelfAssigned', false);
        return;
      }
    }

    // Handle normal employee search
    const exactMatch = employees.find(
      employee => employee.fullname.toLowerCase() === value.toLowerCase()
    );
    if (exactMatch) {
      updateProjectField('assignedEmployee', exactMatch.id);
    }
  };

  // Handle clicks outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close customer dropdown if click is outside
      if (
        customerDropdownRef.current &&
        !customerDropdownRef.current.contains(event.target as Node)
      ) {
        setShowCustomerDropdown(false);
      }

      // Close employee dropdown if click is outside
      if (
        employeeDropdownRef.current &&
        !employeeDropdownRef.current.contains(event.target as Node)
      ) {
        setShowEmployeeDropdown(false);
      }

      // Close status dropdown if click is outside
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
        setShowStatusDropdown(false);
      }
    };

    // Handle Escape key press
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowCustomerDropdown(false);
        setShowEmployeeDropdown(false);
        setShowStatusDropdown(false);
      }
    };

    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);

    // Clean up event listeners
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, []);

  // Add this check near the other permission checks
  const hasFinanceAccess = hasFinancePermission(user);

  // Find the billed date and calculate warranty period
  const findBilledDateAndCalculateWarranty = (projectData: any) => {
    console.log('Calculating warranty period from projectData:', projectData);

    // Check if we have warranty date
    if (!projectData.warrantyUntil) {
      console.log('No warranty date found');
      return;
    }

    const warrantyDate = new Date(projectData.warrantyUntil);
    console.log('Warranty until date:', warrantyDate);

    // Find the BILLED status entry in project assignments
    let billedAssignment = null;

    if (projectData.projectAssigned && projectData.projectAssigned.length > 0) {
      // Look for the most recent BILLED status entry
      billedAssignment = projectData.projectAssigned
        .filter((assignment: any) => assignment.status === 'BILLED')
        .sort((a: any, b: any) => {
          const dateA = new Date(a.assignedDate || a.assignedDateAndTime || 0).getTime();
          const dateB = new Date(b.assignedDate || b.assignedDateAndTime || 0).getTime();
          return dateB - dateA; // Descending order (newest first)
        })[0];
    }

    if (billedAssignment) {
      const billedDateValue = new Date(
        billedAssignment.assignedDate || billedAssignment.assignedDateAndTime
      );
      console.log('Found billed date:', billedDateValue);
      setBilledDate(billedDateValue);

      // Calculate days between billed date and warranty date
      const days = calculateDaysBetween(billedDateValue, warrantyDate);
      console.log('Warranty period in days:', days);
      setWarrantyPeriod(days.toString());
      setOriginalWarrantyPeriod(days.toString()); // Store original value for comparison
    } else {
      console.log('No BILLED status found in project history');
    }
  };

  // Show spinner while loading OR while the access guard hasn't run yet.
  // This prevents showing project data for even a single frame before redirecting.
  if (isLoading || (!loadError && !isAccessChecked)) {
    return <Loading height="calc(100vh - 200px)" />;
  }

  if (loadError) {
    return (
      <ComponentWrapper
        icon={<FiFileText />}
        title={
          <div className="flex items-center gap-3">
            <span>Edit Project</span>
            <span className="text-sm md:text-base font-semibold bg-blue-100 text-blue-700 px-3 py-1 rounded-md">
              {manakId || 'N/A'}
            </span>
          </div>
        }
      >
        <div className="bg-white rounded-lg p-6 text-center">
          <p className="text-red-500">{loadError}</p>
          <button
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg"
            onClick={() => navigate(returnPath)}
          >
            Back
          </button>
        </div>
      </ComponentWrapper>
    );
  }

  return (
    <ComponentWrapper
      icon={<FiFileText />}
      title={
        <div className="flex items-center gap-3">
          <span>Edit Project</span>
          <span className="text-sm md:text-base font-semibold bg-blue-100 text-blue-700 px-3 py-1 rounded-md">
            {manakId || 'Loading...'}
          </span>
        </div>
      }
    >
      <div className="bg-white rounded-lg p-3 sm:p-4 md:p-6">
        {isReadOnly && (
          <div className="mb-4 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <FiEdit className="h-4 w-4 shrink-0" />
            <span>
              <strong>View only</strong> — This project is not assigned to you or your team.
              You can read the details but cannot make changes.
            </span>
          </div>
        )}
        <div className="flex flex-col gap-4 sm:gap-5 md:gap-6 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <InputField
              label="Project Name"
              type="text"
              placeholder="Enter Project Name"
              value={projectData.projectName}
              onChange={e => updateProjectField('projectName', e.target.value)}
              icon={<FiFileText />}
              labelClassName="text-[#8D99A5] text-xs"
              disabled={isReadOnly || (!isAdmin && !hasChangeProjectNamePermission(user))}
              helpText={
                isReadOnly ? undefined
                  : !isAdmin && !hasChangeProjectNamePermission(user)
                  ? "You don't have permission to change project name"
                  : undefined
              }
            />
            <InputField
              label="Article Number"
              type="text"
              placeholder="Enter Article Number"
              value={projectData.articleNumber}
              onChange={e => updateProjectField('articleNumber', e.target.value)}
              icon={<MdOutlineNumbers />}
              labelClassName="text-[#8D99A5] text-xs"
              disabled={isReadOnly || (!isAdmin && !hasChangeArticleNumberPermission(user))}
              helpText={
                isReadOnly ? undefined
                  : !isAdmin && !hasChangeArticleNumberPermission(user)
                  ? "You don't have permission to change article number"
                  : undefined
              }
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="relative" ref={customerDropdownRef}>
              <div className="relative">
                <InputField
                  label="Company Name"
                  type="text"
                  placeholder="Search Company..."
                  value={customerSearch}
                  onChange={handleCustomerSearchChange}
                  icon={<TbBuildingStore />}
                  labelClassName="text-[#8D99A5] text-xs"
                  onClick={() => {
                    if (!isReadOnly && (isAdmin || hasChangeCompanyNamePermission(user))) {
                      setShowCustomerDropdown(true);
                    }
                  }}
                  disabled={isReadOnly || (!isAdmin && !hasChangeCompanyNamePermission(user))}
                  helpText={
                    isReadOnly ? undefined
                      : !isAdmin && !hasChangeCompanyNamePermission(user)
                      ? "You don't have permission to change company name"
                      : undefined
                  }
                />
              </div>
              {showCustomerDropdown && customerOptions.length > 0 && (
                <div className="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200 max-h-60 overflow-y-auto">
                  {customerOptions.map((name, index) => (
                    <div
                      key={index}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => {
                        setCustomerSearch(name);
                        const selectedCustomer = customers.find(c => c.companyName === name);
                        if (selectedCustomer) {
                          updateProjectField('customer', selectedCustomer.id);
                        }
                        setShowCustomerDropdown(false);
                      }}
                    >
                      {name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="relative" ref={employeeDropdownRef}>
              <div className="relative">
                <InputField
                  label="Assigned to Employee"
                  type="text"
                  placeholder="Search Employee..."
                  value={employeeSearch}
                  onChange={handleEmployeeSearchChange}
                  icon={<FiUser />}
                  labelClassName="text-[#8D99A5] text-xs"
                  onClick={() => {
                    if (!isReadOnly && (isAdmin || hasTransferProjectPermission(user))) {
                      setShowEmployeeDropdown(true);
                    }
                  }}
                  disabled={isReadOnly || (!isAdmin && !hasTransferProjectPermission(user))}
                  helpText={
                    isReadOnly ? undefined
                      : !isAdmin && !hasTransferProjectPermission(user)
                      ? "You don't have permission to transfer project"
                      : undefined
                  }
                />
              </div>
              {showEmployeeDropdown && (
                <div className="absolute z-50 mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200 max-h-60 overflow-y-auto">
                  {/* Self Assign option at the top - only show for admin/superAdmin */}
                  {isAdmin && (
                    <div
                      className={`px-4 py-2 ${isAdmin || hasTransferProjectPermission(user)
                        ? 'hover:bg-gray-100 cursor-pointer'
                        : 'cursor-not-allowed opacity-60'
                        } font-medium ${projectData.isSelfAssigned ? 'bg-lime-100' : ''
                        } text-lime-700 border-b border-gray-200`}
                      onClick={() => {
                        if (isAdmin || hasTransferProjectPermission(user)) {
                          // Find current user's full name
                          const currentUser = employees.find(e => e.id === user?.id);
                          const displayText = currentUser
                            ? `You (${currentUser.fullname})`
                            : 'Self Assign';
                          setEmployeeSearch(displayText);
                          updateProjectField('isSelfAssigned', true);
                          // Set to the current user's ID
                          updateProjectField('assignedEmployee', user?.id || 'self');
                          setShowEmployeeDropdown(false);
                        }
                      }}
                    >
                      {user && employees.find(e => e.id === user.id)
                        ? `You (${employees.find(e => e.id === user.id)?.fullname})`
                        : 'Self Assign'}{' '}
                      {projectData.isSelfAssigned && '✓'}
                      {!isAdmin && !hasTransferProjectPermission(user) && (
                        <span className="ml-2 text-xs text-gray-500">(No permission)</span>
                      )}
                    </div>
                  )}
                  {employeeOptions.length > 0 ? (
                    employeeOptions.map((name, index) => {
                      const selectedEmployee = employees.find(e => e.fullname === name);
                      const isSelected =
                        !projectData.isSelfAssigned &&
                        selectedEmployee &&
                        selectedEmployee.id === projectData.assignedEmployee;

                      // Check if this employee is the logged-in user
                      const isCurrentUser = selectedEmployee && selectedEmployee.id === user?.id;

                      // Check if the user has permission to transfer projects
                      const canTransfer = isAdmin || hasTransferProjectPermission(user);

                      return (
                        <div
                          key={index}
                          className={`px-4 py-2 ${canTransfer
                            ? 'hover:bg-gray-100 cursor-pointer'
                            : 'cursor-not-allowed opacity-60'
                            } ${isSelected ? 'bg-blue-100' : ''}`}
                          onClick={() => {
                            if (canTransfer) {
                              // Display "You (name)" format when current user is selected
                              const displayText = isCurrentUser ? `You (${name})` : name;
                              setEmployeeSearch(displayText);
                              updateProjectField('isSelfAssigned', false);
                              if (selectedEmployee) {
                                updateProjectField('assignedEmployee', selectedEmployee.id);
                              }
                              setShowEmployeeDropdown(false);
                            }
                          }}
                        >
                          {isCurrentUser ? `You (${name})` : name} {isSelected && '✓'}
                          {!canTransfer && (
                            <span className="ml-2 text-xs text-gray-500">(No permission)</span>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="px-4 py-2 text-gray-500">No employees found</div>
                  )}
                </div>
              )}
            </div>
          </div>

          <InputField
            label="Project Description"
            type="textarea"
            placeholder="Enter Project Description"
            value={projectData.projectDescription}
            onChange={e => updateProjectField('projectDescription', e.target.value)}
            icon={<MdOutlineDescription />}
            className="min-h-[80px]"
            labelClassName="text-[#8D99A5] text-xs"
            disabled={isReadOnly || (!isAdmin && !hasChangeProjectDescriptionPermission(user))}
            helpText={
              isReadOnly ? undefined
                : !isAdmin && !hasChangeProjectDescriptionPermission(user)
                ? "You don't have permission to change project description"
                : undefined
            }
          />

          <InputField
            label="Select Service"
            type="select"
            placeholder="Select a service"
            value={projectData.projectRemarks}
            options={[
              'Flow Simulation - Moldex',
              'Flow Simulation - MF',
              'FEA Simulation',
              'To be Selected',
              'Moldflow Training',
              'Casting Simulation',
              'Internal Billing',
              'Product Sale',
            ]}
            onChange={e => updateProjectField('projectRemarks', e.target.value)}
            icon={<RiFileTextLine />}
            labelClassName="text-[#8D99A5] text-xs"
            disabled={isReadOnly || (!isAdmin && !hasChangeProjectRemarksPermission(user))}
            helpText={
              isReadOnly ? undefined
                : !isAdmin && !hasChangeProjectRemarksPermission(user)
                ? "You don't have permission to change service"
                : undefined
            }
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="relative">
              <div className="flex items-center gap-2">
                <div className="flex-grow">
                  <InputField
                    label={`Project Status ${projectDataFromState?.projectRevisions?.length > 0 || projectDataFromState?.revisionNumber > 0 ? `(Under revision)` : ``}`}
                    type="select"
                    placeholder="Select Status"
                    value={projectData.projectStatus}
                    onChange={e => updateProjectField('projectStatus', e.target.value)}
                    options={getValidStatusTransitions(
                      statusOptions,
                      originalData.projectStatus,
                      isAdmin
                    ).map(statusOption => {
                      // Format the status for display in the dropdown
                      let displayValue = formatStatus(statusOption.value);

                      // Add note for REOPEN status that it defaults to ASSIGNED when not chargeable
                      if (statusOption.value === 'REOPEN')
                        displayValue = 'Reopen (Default to Assigned)';

                      // Define colors for each status
                      let bgColor = 'bg-white';
                      let textColor = 'text-gray-700';

                      // Assign different colors based on status
                      switch (statusOption.value) {
                        case 'UNASSIGNED':
                          bgColor = 'bg-gray-100';
                          textColor = 'text-gray-700';
                          break;
                        case 'ASSIGNED':
                          bgColor = 'bg-blue-50';
                          textColor = 'text-blue-700';
                          break;
                        case 'IN_PROGRESS':
                          bgColor = 'bg-indigo-50';
                          textColor = 'text-indigo-700';
                          break;
                        case 'REVIEW':
                          bgColor = 'bg-purple-50';
                          textColor = 'text-purple-700';
                          break;
                        case 'DELIVERED':
                          bgColor = 'bg-green-50';
                          textColor = 'text-green-700';
                          break;
                        case 'BILLED':
                          bgColor = 'bg-yellow-50';
                          textColor = 'text-yellow-700';
                          break;
                        case 'HOLD':
                          bgColor = 'bg-orange-50';
                          textColor = 'text-orange-700';
                          break;
                        case 'CANCELLED':
                          bgColor = 'bg-red-50';
                          textColor = 'text-red-700';
                          break;
                        case 'PAYMENT_RECEIVED':
                          bgColor = 'bg-emerald-50';
                          textColor = 'text-emerald-700';
                          break;
                        case 'REOPEN':
                          bgColor = 'bg-amber-50';
                          textColor = 'text-amber-700';
                          break;
                        case 'NON_ACTIVE':
                          bgColor = 'bg-slate-100';
                          textColor = 'text-slate-700';
                          break;
                        default:
                          bgColor = 'bg-white';
                          textColor = 'text-gray-700';
                      }

                      // Return option object with both value and label, plus the styling classes and disabled state
                      return {
                        value: statusOption.value,
                        label: displayValue,
                        className: `${bgColor} ${textColor} font-medium`,
                        disabled: statusOption.disabled,
                      };
                    })}
                    icon={<BsListCheck />}
                    labelClassName="text-[#8D99A5] text-xs"
                    disabled={isReadOnly}
                  />
                </div>
                {/* Help tooltip - moved to be inline with the field */}
                <button
                  type="button"
                  onClick={() => setShowStatusHelpModal(true)}
                  className="text-blue-500 hover:text-blue-600 transition-colors mt-2"
                  title="View Status Transition Rules"
                >
                  <FiHelpCircle size={20} />
                </button>
              </div>
            </div>

            {/* Cost input field - moved next to Project Status - only show if user has finance permission */}
            {hasFinanceAccess && (
              <InputField
                label="Project Cost"
                type="number"
                value={projectData.projectCost}
                onChange={e => updateProjectField('projectCost', e.target.value)}
                placeholder="Enter project cost"
                icon={<BsCurrencyRupee />}
                min="0"
                disabled={isReadOnly || (!isAdmin && !hasChangeProjectCostPermission(user))}
                helpText={
                  isReadOnly ? undefined
                    : !isAdmin && !hasChangeProjectCostPermission(user)
                    ? "You don't have permission to change project cost"
                    : undefined
                }
                className="no-spinner"
                onWheel={e => e.currentTarget.blur()}
              />
            )}

            {/* Warranty Period field - only show if project status is BILLED or has been billed (has billedDate) */}
            {(projectData.projectStatus === 'BILLED' || billedDate) && (
              <InputField
                label="Warranty Period (Days)"
                type="number"
                placeholder="Enter warranty period in days"
                value={warrantyPeriod}
                onChange={e => setWarrantyPeriod(e.target.value)}
                icon={<AiOutlineClockCircle className="h-4 w-4" />}
                labelClassName="text-[#8D99A5] text-xs"
                min="0"
                disabled={
                  isReadOnly ||
                  (!isAdmin &&
                    !hasChangeWarrantyPeriodPermission(user) &&
                    !hasStatusPermission(user, 'BILLED'))
                }
                helpText={
                  isReadOnly ? undefined
                    : !isAdmin &&
                      !hasChangeWarrantyPeriodPermission(user) &&
                      !hasStatusPermission(user, 'BILLED')
                    ? "You don't have permission to change warranty period"
                    : undefined
                }
                className="no-spinner"
                onWheel={e => e.currentTarget.blur()}
              />
            )}

            {/* View History Button */}
            <div className="col-span-2">
              <button
                type="button"
                onClick={() => setShowAssignmentHistory(!showAssignmentHistory)}
                className={`w-full px-4 py-3 rounded-lg transition-colors border-2 
                  flex items-center justify-between
                  ${showAssignmentHistory
                    ? 'bg-blue-50 border-blue-600 text-blue-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <FiClock className="h-5 w-5" />
                  <span className="font-medium">View History</span>
                </div>
                {showAssignmentHistory ? (
                  <FiChevronUp className="h-5 w-5" />
                ) : (
                  <FiChevronDown className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {/* Assignment History Table */}
          <div
            className={`mt-3 transition-all duration-300 ${showAssignmentHistory ? 'max-h-[800px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}
          >
            <TaskAssignmentTable
              assignments={assignmentHistory}
              visible={showAssignmentHistory}
            />
          </div>

          {/* Display billing and warranty information when they exist or when status is BILLED */}
          {(projectData.billNumber ||
            projectDataFromState?.warrantyUntil ||
            projectData.projectStatus === 'BILLED') && (
              <div className="bg-white rounded-lg shadow-sm p-6 border-2 border-gray-200">
                <div className="flex items-center gap-3 mb-6">
                  <BiReceipt className="h-6 w-6 text-gray-800" />
                  <h3 className="text-sm font-medium text-gray-800">
                    {projectData.projectStatus === 'BILLED' ? (
                      <>
                        Project has been transferred from{' '}
                        <u>
                          <i>
                            <b>Delieverd</b>
                          </i>
                        </u>{' '}
                        to{' '}
                        <u>
                          <i>
                            <b>Billed</b>
                          </i>
                        </u>
                        . Please Fill the Bill Number to proceed.
                      </>
                    ) : (
                      'Billing & Warranty Information'
                    )}
                  </h3>
                </div>

                <div className="space-y-2">
                  <InputField
                    label="Bill Number"
                    type="text"
                    placeholder="Auto-generated from invoice"
                    value={projectData.billNumber}
                    onChange={e => updateProjectField('billNumber', e.target.value)}
                    icon={<FiCheckSquare className="h-4 w-4" />}
                    labelClassName="text-[#8D99A5] text-xs"
                    disabled={true}
                    helpText="Bill number is automatically generated when invoice is created and cannot be manually changed"
                  />

                  {projectDataFromState?.warrantyUntil && (
                    <div className="flex items-center mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                      <AiOutlineClockCircle className="h-5 w-5 text-blue-600 mr-2" />
                      <div>
                        <p className="text-sm font-medium text-blue-800">Warranty Information</p>
                        <p className="text-xs text-blue-600">
                          Warranty Valid Until:{' '}
                          {new Date(projectDataFromState.warrantyUntil).toLocaleDateString()}
                        </p>
                        {billedDate && (
                          <p className="text-xs text-blue-600 mt-1">
                            Billed Date: {billedDate.toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

          <div className="mt-2 sm:mt-4">
            <label className="block text-[#8D99A5] text-xs mb-2">Project Image</label>
            <ImageUploader
              onImageUpload={handleImageUpload}
              onImageRemove={handleImageRemove}
              previewUrl={projectData.imagePreview}
              title="Drop project image here, or click to upload"
              subTitle="Recommended size: 1200×630px"
              dragActiveTitle="Drop project image here"
              height="h-40 sm:h-48 md:h-56"
              disabled={isReadOnly || (!isAdmin && !hasUploadProductImagePermission(user))}
            />
            {!isReadOnly && !isAdmin && !hasUploadProductImagePermission(user) && (
              <p className="text-gray-500 text-xs mt-1">
                You don't have permission to upload project images
              </p>
            )}
          </div>
        </div>

        {projectData.projectStatus === 'REOPEN' && (
          <>
            <div className="mt-6 sm:mt-8 bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <div className="flex items-center gap-3 mb-6">
                <FiRefreshCw className="h-6 w-6 text-gray-800" />
                <h3 className="text-xl font-bold text-gray-800">Revision Details</h3>
              </div>

              {/* Revision Reason */}
              <div className="mb-6 relative">
                <div className="border border-gray-200 rounded-lg p-3 bg-white shadow-sm pt-5">
                  <label className="absolute -top-[10px] left-[12px] bg-white px-1 text-[#8D99A5] text-xs flex items-center gap-1">
                    <FiEdit className="h-3.5 w-3.5" /> Revision Reason
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <button
                      type="button"
                      onClick={() => {
                        if (isAdmin || hasChangeRevisionReasonPermission(user) || hasStatusPermission(user, 'REOPEN')) {
                          updateProjectField('revisionReason', 'feed');
                        }
                      }}
                      className={`flex items-center justify-center gap-2 p-2.5 rounded-lg border transition ${projectData.revisionReason === 'feed'
                        ? 'bg-green-50 border-green-500 text-green-600'
                        : 'border-gray-200 hover:bg-gray-50'
                        } ${!isAdmin && !hasChangeRevisionReasonPermission(user) && !hasStatusPermission(user, 'REOPEN') ? 'opacity-60 cursor-not-allowed' : ''}`}
                      disabled={!isAdmin && !hasChangeRevisionReasonPermission(user) && !hasStatusPermission(user, 'REOPEN')}
                    >
                      <div className="w-5 h-5 text-green-500">
                        {projectData.revisionReason === 'feed' && (
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M20 6L9 17L4 12"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                      Feed System Change
                      {!isAdmin && !hasChangeRevisionReasonPermission(user) && !hasStatusPermission(user, 'REOPEN') && (
                        <span className="text-xs text-gray-500">(No permission)</span>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        if (isAdmin || hasChangeRevisionReasonPermission(user) || hasStatusPermission(user, 'REOPEN')) {
                          updateProjectField('revisionReason', 'cad');
                        }
                      }}
                      className={`flex items-center justify-center gap-2 p-2.5 rounded-lg border transition ${projectData.revisionReason === 'cad'
                        ? 'bg-blue-50 border-blue-500 text-blue-600'
                        : 'border-gray-200 hover:bg-gray-50'
                        } ${!isAdmin && !hasChangeRevisionReasonPermission(user) && !hasStatusPermission(user, 'REOPEN') ? 'opacity-60 cursor-not-allowed' : ''}`}
                      disabled={!isAdmin && !hasChangeRevisionReasonPermission(user) && !hasStatusPermission(user, 'REOPEN')}
                    >
                      <div className="w-5 h-5 text-blue-500">
                        {projectData.revisionReason === 'cad' && (
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M20 6L9 17L4 12"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                      CAD Data Change
                      {!isAdmin && !hasChangeRevisionReasonPermission(user) && !hasStatusPermission(user, 'REOPEN') && (
                        <span className="text-xs text-gray-500">(No permission)</span>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        if (isAdmin || hasChangeRevisionReasonPermission(user) || hasStatusPermission(user, 'REOPEN')) {
                          updateProjectField('revisionReason', 'material');
                        }
                      }}
                      className={`flex items-center justify-center gap-2 p-2.5 rounded-lg border transition ${projectData.revisionReason === 'material'
                        ? 'bg-gray-50 border-gray-500 text-gray-600'
                        : 'border-gray-200 hover:bg-gray-50'
                        } ${!isAdmin && !hasChangeRevisionReasonPermission(user) && !hasStatusPermission(user, 'REOPEN') ? 'opacity-60 cursor-not-allowed' : ''}`}
                      disabled={!isAdmin && !hasChangeRevisionReasonPermission(user) && !hasStatusPermission(user, 'REOPEN')}
                    >
                      <div className="w-5 h-5 text-gray-500">
                        {projectData.revisionReason === 'material' && (
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                              d="M20 6L9 17L4 12"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                      Material Change
                      {!isAdmin && !hasChangeRevisionReasonPermission(user) && !hasStatusPermission(user, 'REOPEN') && (
                        <span className="text-xs text-gray-500">(No permission)</span>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <div>
                {/* Chargeable Section */}
                <div className="relative">
                  <div className="border border-gray-200 rounded-lg p-3 bg-white shadow-sm pt-5">
                    <label className="absolute -top-[10px] left-[12px] bg-white px-1 text-[#8D99A5] text-xs flex items-center gap-1">
                      <BsCurrencyRupee className="h-3.5 w-3.5" /> Chargeable
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => {
                          if (isAdmin || hasChangeRevisionChargeablePermission(user) || hasStatusPermission(user, 'REOPEN')) {
                            updateProjectField('isChargeable', true);
                          }
                        }}
                        className={`flex items-center justify-center gap-2 p-2.5 rounded-lg border transition ${projectData.isChargeable
                          ? 'bg-green-50 border-green-500 text-green-600'
                          : 'border-gray-200 hover:bg-gray-50'
                          } ${!isAdmin && !hasChangeRevisionChargeablePermission(user) && !hasStatusPermission(user, 'REOPEN') ? 'opacity-60 cursor-not-allowed' : ''}`}
                        disabled={!isAdmin && !hasChangeRevisionChargeablePermission(user) && !hasStatusPermission(user, 'REOPEN')}
                      >
                        <div className="w-5 h-5 text-green-500">
                          {projectData.isChargeable && (
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="M20 6L9 17L4 12"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </div>
                        Yes
                        {!isAdmin && !hasChangeRevisionChargeablePermission(user) && !hasStatusPermission(user, 'REOPEN') && (
                          <span className="text-xs text-gray-500">(No permission)</span>
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          if (isAdmin || hasChangeRevisionChargeablePermission(user) || hasStatusPermission(user, 'REOPEN')) {
                            updateProjectField('isChargeable', false);
                          }
                        }}
                        className={`flex items-center justify-center gap-2 p-2.5 rounded-lg border transition ${!projectData.isChargeable
                          ? 'bg-red-50 border-red-500 text-red-600'
                          : 'border-gray-200 hover:bg-gray-50'
                          } ${!isAdmin && !hasChangeRevisionChargeablePermission(user) && !hasStatusPermission(user, 'REOPEN') ? 'opacity-60 cursor-not-allowed' : ''}`}
                        disabled={!isAdmin && !hasChangeRevisionChargeablePermission(user) && !hasStatusPermission(user, 'REOPEN')}
                      >
                        <div className="w-5 h-5 text-red-500">
                          {!projectData.isChargeable && (
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="M18 6L6 18M6 6L18 18"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </div>
                        No
                        {!isAdmin && !hasChangeRevisionChargeablePermission(user) && !hasStatusPermission(user, 'REOPEN') && (
                          <span className="text-xs text-gray-500">(No permission)</span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {projectData.isChargeable && (
                <div className="mt-4 space-y-2">
                  <InputField
                    label="Revision Project Amount"
                    type="number"
                    placeholder="Enter Amount for New Revision Project"
                    value={revisionAmount}
                    onChange={e => setRevisionAmount(e.target.value)}
                    icon={<BsCurrencyRupee className="h-4 w-4" />}
                    labelClassName="text-[#8D99A5] text-xs"
                    disabled={!isAdmin && !hasChangeRevisionAmountPermission(user) && !hasStatusPermission(user, 'REOPEN')}
                    helpText={
                      !isAdmin && !hasChangeRevisionAmountPermission(user) && !hasStatusPermission(user, 'REOPEN')
                        ? "You don't have permission to change revision amount"
                        : undefined
                    }
                    className="no-spinner"
                    onWheel={e => e.currentTarget.blur()}
                  />
                  <p className="text-xs text-gray-500 italic ml-1">
                    This amount will be used for the new revision project. The original project will
                    keep its current amount.
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        <div className="mt-6 sm:mt-8 flex justify-center gap-4">
          <button
            onClick={() => navigate(returnPath)}
            className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-100 text-base sm:text-lg font-semibold rounded-lg hover:bg-gray-200 transition-colors
            flex items-center border-2 border-gray-300 justify-center gap-2 text-gray-700 w-full md:w-1/3"
          >
            {isReadOnly ? 'Back' : 'Cancel'}
          </button>
          {!isReadOnly && (
            <button
              onClick={handleSubmit}
              className="px-4 sm:px-6 py-2 sm:py-3 bg-lime-100 text-base sm:text-lg font-semibold rounded-lg hover:bg-lime-200 transition-colors
              flex items-center border-2 w-full md:w-2/3 border-lime-600 justify-center gap-2 text-lime-700"
            >
              Update Project
            </button>
          )}
        </div>
      </div>

      {/* Status Transition Help Modal */}
      <StatusTransitionHelpModal
        isOpen={showStatusHelpModal}
        onClose={() => setShowStatusHelpModal(false)}
      />
    </ComponentWrapper>
  );
};

export default EditProject;
